import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, UserPlus, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "FIND MISSING GUESTS" dialog — surfaces people who signed up for the event
 * (via the event-level waitlist OR a different promoter's list) but aren't
 * on THIS promoter's list. Lets the promoter bulk-import event-waitlist
 * entries as waitlisted guests so they can approve them normally.
 *
 * Fixes the recurring "stag got the waitlist email but isn't on my list" pain.
 */
export default function RecoveryDialog({ open, onOpenChange, list, token, onImported }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!list?.id || !token) return;
    setLoading(true);
    setSelected(new Set());
    try {
      const res = await axios.get(`${API}/guest-lists/${list.id}/recovery-candidates`, { headers: authHeaders });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch recovery candidates');
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list?.id, token]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const ewl = data?.event_waitlist || [];
  const others = data?.other_lists || [];
  // Only event-waitlist rows that aren't already on this list are importable.
  const importable = ewl.filter((r) => !r.already_here);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === importable.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(importable.map((r) => r.entry_id)));
    }
  };

  const handleImport = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one guest to recover');
      return;
    }
    setImporting(true);
    try {
      const res = await axios.post(
        `${API}/guest-lists/${list.id}/import-from-event-waitlist`,
        { entry_ids: Array.from(selected) },
        { headers: authHeaders },
      );
      toast.success(`Recovered ${res.data.imported} · skipped ${res.data.skipped}`);
      setSelected(new Set());
      onImported?.();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border-zinc-800 text-white w-[calc(100vw-1.5rem)] sm:max-w-3xl max-h-[90vh] overflow-y-auto p-4 sm:p-6"
        data-testid="recovery-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-['Josefin_Sans'] text-xl">FIND MISSING GUESTS</DialogTitle>
        </DialogHeader>

        <p className="text-zinc-400 text-xs mt-2 flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0 text-[#B5FF00]" />
          Shows people who signed up for <span className="text-white font-mono">{data?.event_title || 'this event'}</span> through other paths — the event-level waitlist form or another promoter&apos;s link.
        </p>

        {loading && (
          <div className="flex items-center justify-center py-12 text-zinc-500 text-xs font-mono">
            <Loader2 size={16} className="animate-spin mr-2" /> SEARCHING…
          </div>
        )}

        {!loading && data && (
          <div className="space-y-6 mt-4">
            {/* Event-waitlist bucket */}
            <section data-testid="recovery-ewl-section">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-mono text-xs text-zinc-400 tracking-widest">
                  EVENT WAITLIST SIGNUPS · {ewl.length}
                </h3>
                {importable.length > 0 && (
                  <button
                    onClick={toggleAll}
                    className="font-mono text-[11px] text-[#B5FF00] hover:underline tracking-wider"
                    data-testid="recovery-select-all"
                  >
                    {selected.size === importable.length ? 'DESELECT ALL' : 'SELECT ALL'}
                  </button>
                )}
              </div>

              {ewl.length === 0 && (
                <p className="text-zinc-500 text-xs">No one signed up via the event-level waitlist form.</p>
              )}

              {ewl.length > 0 && (
                <div className="border border-zinc-800">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-800 bg-[#050505]">
                        <th className="w-10 py-2 px-2"></th>
                        <th className="text-left py-2 px-2 font-mono text-[10px] text-zinc-500 tracking-widest">NAME</th>
                        <th className="text-left py-2 px-2 font-mono text-[10px] text-zinc-500 tracking-widest hidden sm:table-cell">CONTACT</th>
                        <th className="text-left py-2 px-2 font-mono text-[10px] text-zinc-500 tracking-widest">#</th>
                        <th className="text-left py-2 px-2 font-mono text-[10px] text-zinc-500 tracking-widest">STATUS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ewl.map((r) => (
                        <tr key={r.entry_id} className="border-b border-zinc-900" data-testid={`recovery-ewl-row-${r.entry_id}`}>
                          <td className="py-2 px-2">
                            {!r.already_here && (
                              <input
                                type="checkbox"
                                checked={selected.has(r.entry_id)}
                                onChange={() => toggle(r.entry_id)}
                                className="accent-[#B5FF00] w-4 h-4"
                                data-testid={`recovery-check-${r.entry_id}`}
                              />
                            )}
                          </td>
                          <td className="py-2 px-2 text-white text-sm">
                            {r.name}
                            <div className="sm:hidden text-zinc-500 text-xs font-mono truncate max-w-[180px]">
                              {r.phone || r.email}
                            </div>
                          </td>
                          <td className="py-2 px-2 text-zinc-400 font-mono text-xs hidden sm:table-cell">
                            <div className="truncate max-w-[200px]">{r.email}</div>
                            <div>{r.phone}</div>
                          </td>
                          <td className="py-2 px-2 text-zinc-500 font-mono text-xs">{r.queue_number ?? '—'}</td>
                          <td className="py-2 px-2">
                            {r.already_here ? (
                              <span className="px-1.5 py-0.5 text-[10px] font-mono bg-[#B5FF00]/10 text-[#B5FF00] border border-[#B5FF00]/30">
                                ON LIST
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.5 text-[10px] font-mono bg-amber-500/10 text-amber-400 border border-amber-500/30">
                                MISSING
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Other promoter lists bucket (read-only — can't import across promoters) */}
            <section data-testid="recovery-other-lists-section">
              <h3 className="font-mono text-xs text-zinc-400 tracking-widest mb-2">
                ON OTHER PROMOTERS&apos; LISTS · {others.length}
              </h3>

              {others.length === 0 && (
                <p className="text-zinc-500 text-xs">No one on another promoter&apos;s list for this event.</p>
              )}

              {others.length > 0 && (
                <div className="border border-zinc-800">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-800 bg-[#050505]">
                        <th className="text-left py-2 px-2 font-mono text-[10px] text-zinc-500 tracking-widest">NAME</th>
                        <th className="text-left py-2 px-2 font-mono text-[10px] text-zinc-500 tracking-widest hidden sm:table-cell">CONTACT</th>
                        <th className="text-left py-2 px-2 font-mono text-[10px] text-zinc-500 tracking-widest">LIST</th>
                        <th className="text-left py-2 px-2 font-mono text-[10px] text-zinc-500 tracking-widest">STATUS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {others.map((r) => (
                        <tr key={r.entry_id} className="border-b border-zinc-900" data-testid={`recovery-other-row-${r.entry_id}`}>
                          <td className="py-2 px-2 text-white text-sm">{r.name}</td>
                          <td className="py-2 px-2 text-zinc-400 font-mono text-xs hidden sm:table-cell">
                            <div className="truncate max-w-[200px]">{r.email}</div>
                            <div>{r.phone}</div>
                          </td>
                          <td className="py-2 px-2 font-mono text-xs">
                            <div className="text-zinc-300">{r.list_name}</div>
                            <div className="text-zinc-500">{r.list_code}</div>
                          </td>
                          <td className="py-2 px-2">
                            <span className={`px-1.5 py-0.5 text-[10px] font-mono border ${
                              r.status === 'waitlisted'
                                ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                                : 'bg-[#B5FF00]/10 text-[#B5FF00] border-[#B5FF00]/30'
                            }`}>
                              {(r.status || 'confirmed').toUpperCase()}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}

        <div className="flex gap-3 pt-5 border-t border-zinc-800 mt-5 sticky bottom-0 bg-[#0A0A0A]">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="btn-secondary flex-1"
          >
            CLOSE
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={importing || selected.size === 0}
            className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-40"
            data-testid="recovery-import-btn"
          >
            {importing ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
            RECOVER {selected.size > 0 ? `(${selected.size})` : ''} TO MY LIST
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
